import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './CallbackModal.css';

const MOBILE_RE = /^[6-9]\d{9}$/;

export default function CallbackModal({ open, onClose }) {
  const overlayRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setErrors({});
    }
  }, [open]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const validate = (data) => {
    const next = {};
    if (!data.get('fullName')?.trim()) next.fullName = 'Please enter your full name.';
    const email = data.get('email')?.trim() || '';
    if (!email) next.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email.';
    const mobile = (data.get('mobile') || '').replace(/\D/g, '');
    if (!mobile) next.mobile = 'Please enter your mobile number.';
    else if (!MOBILE_RE.test(mobile)) next.mobile = 'Enter a valid 10-digit Indian mobile (starts 6–9).';
    if (!data.get('location')?.trim()) next.location = 'Please enter your plot location.';
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    const nextErrors = validate(data);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = {
      fullName: data.get('fullName')?.trim(),
      email: data.get('email')?.trim(),
      mobile: `+91 ${(data.get('mobile') || '').replace(/\D/g, '')}`,
      location: data.get('location')?.trim(),
    };
    // Ready for API: POST /api/leads etc.
    console.info('[Plan Your Project]', payload);
    setSubmitted(true);
    form.reset();
  };

  const handleClose = () => {
    setSubmitted(false);
    setErrors({});
    onClose();
  };

  return (
    <div
      className="callback-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="callback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="callback-title"
      >
        <button type="button" className="callback-close" onClick={handleClose} aria-label="Close dialog">
          <span className="callback-close-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </button>

        {!submitted ? (
          <div className="callback-modal-header" aria-hidden="true">
            <div className="callback-modal-header-pattern" />
            <div className="callback-modal-header-glow" />
            <p className="callback-modal-eyebrow">Landmarks Construction</p>
            <div className="callback-modal-header-accent" />
          </div>
        ) : null}

        <div className={`callback-inner${submitted ? ' callback-inner--success' : ''}`}>
          {submitted ? (
            <div className="callback-success">
              <div className="callback-success-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M8 12l2.5 2.5L16 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 id="callback-title" className="callback-title">
                You&apos;re on the list
              </h2>
              <p className="callback-subtitle">
                We have received your details. Our construction advisor will reach out shortly.
              </p>
              <button type="button" className="callback-submit" onClick={handleClose}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="callback-intro">
                <h2 id="callback-title" className="callback-title">
                  Request a site callback
                </h2>
                <p className="callback-subtitle">
                  Share a few details—we&apos;ll call you back with next steps for your plot or project.
                </p>
              </div>
              <form className="callback-form" onSubmit={handleSubmit} noValidate>
                <label className="callback-field">
                  <span className="sr-only">Full name (required)</span>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name*"
                    autoComplete="name"
                    aria-invalid={errors.fullName ? 'true' : 'false'}
                    aria-describedby={errors.fullName ? 'err-fullName' : undefined}
                  />
                  {errors.fullName ? (
                    <span id="err-fullName" className="callback-error" role="alert">
                      {errors.fullName}
                    </span>
                  ) : null}
                </label>

                <label className="callback-field">
                  <span className="sr-only">Email (required)</span>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email*"
                    autoComplete="email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'err-email' : undefined}
                  />
                  {errors.email ? (
                    <span id="err-email" className="callback-error" role="alert">
                      {errors.email}
                    </span>
                  ) : null}
                </label>

                <label className="callback-field">
                  <span className="sr-only">Mobile number (required)</span>
                  <div className="callback-phone-row">
                    <span className="callback-phone-prefix" aria-hidden="true">
                      <span className="callback-phone-flag">🇮🇳</span>
                      <span>+91</span>
                    </span>
                    <input
                      type="tel"
                      name="mobile"
                      className="callback-phone-input"
                      placeholder="Mobile Number*"
                      autoComplete="tel-national"
                      inputMode="numeric"
                      maxLength={10}
                      aria-invalid={errors.mobile ? 'true' : 'false'}
                      aria-describedby={errors.mobile ? 'err-mobile' : undefined}
                    />
                  </div>
                  {errors.mobile ? (
                    <span id="err-mobile" className="callback-error" role="alert">
                      {errors.mobile}
                    </span>
                  ) : null}
                </label>

                <label className="callback-field">
                  <span className="sr-only">Location of your plot (required)</span>
                  <input
                    type="text"
                    name="location"
                    placeholder="Location of your plot*"
                    autoComplete="street-address"
                    aria-invalid={errors.location ? 'true' : 'false'}
                    aria-describedby={errors.location ? 'err-location' : undefined}
                  />
                  {errors.location ? (
                    <span id="err-location" className="callback-error" role="alert">
                      {errors.location}
                    </span>
                  ) : null}
                </label>

                <button type="submit" className="callback-submit callback-submit--wide">
                  Send request
                  <span className="callback-submit-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                      <path
                        d="M5 12h12M13 7l5 5-5 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>

                <p className="callback-disclaimer">
                  By submitting, you agree to our{' '}
                  <Link to="/privacy" onClick={handleClose}>
                    Privacy Policy
                  </Link>
                  .
                </p>
              </form>

              <ul className="callback-trust" aria-label="Why Landmarks">
                <li className="callback-trust-chip">
                  <span className="callback-trust-dot" aria-hidden="true" />
                  120+ projects
                </li>
                <li className="callback-trust-chip">
                  <span className="callback-trust-dot" aria-hidden="true" />
                  Delhi NCR
                </li>
                <li className="callback-trust-chip">
                  <span className="callback-trust-dot" aria-hidden="true" />
                  Clear estimates
                </li>
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
